import React, { ChangeEvent } from 'react';

import { RadioChangeEvent } from 'antd';
import styled from 'styled-components';

import { DatePicker } from 'components/DatePicker/DatePicker';
import Input from 'components/lib/Input';
import Radio from 'components/lib/Radio';

import { useShopRequestModalContext } from './ShopRequestModalContext';
import ShopRequestModalUserSelect from './ShopRequestModalUserSelect';
import { USER_GROUPS } from '../../../constants';
import { MYSTERY_SHOP_REQUEST_DUE_DATES } from '../constants';
import { useShopRequestsContext } from '../ShopRequestsContext';
import ShopRequestsRepeatPicker from '../ShopRequestsRepeatPicker';

const Container = styled.div`
  padding: 20px 30px 15px 20px;
  display: flex;
  flex-flow: column;
  max-width: 370px;
  box-sizing: border-box;
`;

const StyledTitle = styled.div`
  font-family: var(--font-bold);
  margin-bottom: 10px;
`;

const DatesContainer = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  margin-top: 20px;
  margin-bottom: 30px;
  min-height: 200px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-flow: column;
`;

const StyledRadioButton = styled(Radio)`
  font-family: var(--font-regular);
  font-size: 14px;
  margin-bottom: 10px;
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledInput = styled(Input)`
  width: 320px;
`;

const ShopRequestModalInfo = () => {
  const {
    dueDate,
    repeat,
    name,
    specificDueDate,
    repeatEnabled,
    endRepeat,
    shopperId,
    auditorId,
    setAuditorId,
    setShopperId,
    setEndRepeat,
    setRepeatEnabled,
    setSpecificDueDate,
    setDueDate,
    setName,
    setRepeat,
  } = useShopRequestModalContext();

  const { staffUsers } = useShopRequestsContext();
  const mysteryShopAdmins = staffUsers?.filter((u) => u.groups?.includes(USER_GROUPS.MYSTERY_SHOP_ADMIN)) ?? [];
  const shoppers = staffUsers?.filter((u) => !u.groups?.includes(USER_GROUPS.MYSTERY_SHOP_ADMIN)) ?? [];

  return (
    <Container>
      <StyledTitle>Name (Optional)</StyledTitle>
      <StyledInput
        placeholder="Enter an optional name for easier search"
        value={name || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
      />
      <DatesContainer>
        <div>
          <StyledTitle>Due date</StyledTitle>
          <StyledRadioGroup onChange={(event: RadioChangeEvent) => setDueDate(event.target.value)} value={dueDate}>
            <StyledRadioButton value={MYSTERY_SHOP_REQUEST_DUE_DATES.IN_3_DAYS}>In 3 days</StyledRadioButton>
            <StyledRadioButton value={MYSTERY_SHOP_REQUEST_DUE_DATES.IN_A_WEEK}>In a week</StyledRadioButton>
            <StyledRadioButton value={MYSTERY_SHOP_REQUEST_DUE_DATES.IN_2_WEEKS}>In 2 weeks</StyledRadioButton>
            <StyledRadioButton value={MYSTERY_SHOP_REQUEST_DUE_DATES.SPECIFIC_DATE}>Specific date</StyledRadioButton>
          </StyledRadioGroup>
          {dueDate === MYSTERY_SHOP_REQUEST_DUE_DATES.SPECIFIC_DATE && (
            <DatePicker selectedDate={specificDueDate} onChange={setSpecificDueDate} />
          )}
        </div>
        <ShopRequestsRepeatPicker
          repeat={repeat}
          endRepeat={endRepeat}
          repeatEnabled={repeatEnabled}
          setRepeat={setRepeat}
          setEndRepeat={setEndRepeat}
          setRepeatEnabled={setRepeatEnabled}
        />
      </DatesContainer>
      <StyledTitle>Shopper (Optional)</StyledTitle>
      <ShopRequestModalUserSelect value={shopperId} onChange={setShopperId} users={shoppers} />
      <StyledTitle>Auditor (Optional)</StyledTitle>
      <ShopRequestModalUserSelect value={auditorId} onChange={setAuditorId} users={mysteryShopAdmins} />
    </Container>
  );
};

export default ShopRequestModalInfo;
